import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Drawer,
  FileButton,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconEdit, IconPlus, IconPaperclip, IconTrash } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

// Phase HR-1: الموظفون — the employee registry feeding payroll.

export interface EmployeeAttachmentDto {
  employeeAttachmentId: string;
  fileName: string;
  url: string;
  kind: string | null;
  createdAt: string;
}

export interface EmployeeDto {
  employeeId: string;
  fullNameAr: string;
  fullNameEn: string | null;
  nationalId: string | null;
  phone: string | null;
  positionAr: string;
  hireDate: string;
  baseSalary: number;
  isActive: boolean;
  notes: string | null;
  dateOfBirth: string | null;
  gender: number | null;
  maritalStatus: number | null;
  address: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  bankName: string | null;
  bankIban: string | null;
  bankAccountNumber: string | null;
  attachments: EmployeeAttachmentDto[];
}

export interface LeaveBalanceDto {
  yearsOfService: number;
  annualEntitlement: number;
  annualUsed: number;
  annualRemaining: number;
  sickFullPayEntitlement: number;
  sickUsed: number;
  sickFullPayRemaining: number;
  sickHalfPayEntitlement: number;
}

const QK = ['employees'] as const;

export function useEmployees(includeInactive: boolean) {
  return useQuery({
    queryKey: [...QK, includeInactive] as const,
    queryFn: async () =>
      (
        await apiClient.get<EmployeeDto[]>('/api/hr/employees', {
          params: { includeInactive },
        })
      ).data,
  });
}

export function EmployeesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeDto | null>(null);

  const { data, isLoading } = useEmployees(showInactive);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (e: EmployeeDto) => { setEditing(e); setDrawerOpen(true); };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.hr')}
        title={t('hr.employees.title')}
        subtitle={t('hr.employees.subtitle')}
      />

      <Paper p="md">
        <Group justify="space-between">
          <Switch
            label={t('hr.employees.showInactive')}
            checked={showInactive}
            onChange={(e) => setShowInactive(e.currentTarget.checked)}
          />
          <Button leftSection={<IconPlus size={16} stroke={1.6} />} onClick={openCreate}>
            {t('hr.employees.create')}
          </Button>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('hr.employees.columns.name')}</Table.Th>
                <Table.Th>{t('hr.employees.columns.position')}</Table.Th>
                <Table.Th>{t('hr.employees.columns.phone')}</Table.Th>
                <Table.Th>{t('hr.employees.columns.hireDate')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('hr.employees.columns.salary')}</Table.Th>
                <Table.Th>{t('hr.employees.columns.status')}</Table.Th>
                <Table.Th style={{ width: 60 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((e) => (
                <Table.Tr key={e.employeeId} style={{ opacity: e.isActive ? 1 : 0.55 }}>
                  <Table.Td><Text size="sm" fw={500}>{e.fullNameAr}</Text></Table.Td>
                  <Table.Td>{e.positionAr}</Table.Td>
                  <Table.Td className="font-numeric">{e.phone ?? '—'}</Table.Td>
                  <Table.Td className="font-numeric">{e.hireDate}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {e.baseSalary.toFixed(2)}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light" radius="sm"
                      color={e.isActive ? 'green' : 'red'}>
                      {e.isActive ? t('hr.employees.active') : t('hr.employees.inactive')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button size="compact-sm" variant="subtle"
                      leftSection={<IconEdit size={14} stroke={1.6} />}
                      onClick={() => openEdit(e)}>
                      {t('common.edit')}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('hr.employees.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <EmployeeDrawer
        opened={drawerOpen}
        employee={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => qc.invalidateQueries({ queryKey: QK })}
      />
    </Stack>
  );
}

function EmployeeDrawer({
  opened, employee, onClose, onSaved,
}: {
  opened: boolean;
  employee: EmployeeDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const editing = employee !== null;
  const [nameAr, setNameAr] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [hireDate, setHireDate] = useState<Date | null>(new Date());
  const [salary, setSalary] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);
  // Personal file
  const [dob, setDob] = useState<Date | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [marital, setMarital] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [emgName, setEmgName] = useState('');
  const [emgPhone, setEmgPhone] = useState('');
  // Bank
  const [bankName, setBankName] = useState('');
  const [bankIban, setBankIban] = useState('');
  const [bankAccount, setBankAccount] = useState('');

  // Sync form when the drawer opens for a different employee.
  const [lastKey, setLastKey] = useState<string | null>(null);
  const key = `${opened}-${employee?.employeeId ?? 'new'}`;
  if (opened && key !== lastKey) {
    setLastKey(key);
    setNameAr(employee?.fullNameAr ?? '');
    setPosition(employee?.positionAr ?? '');
    setPhone(employee?.phone ?? '');
    setNationalId(employee?.nationalId ?? '');
    setHireDate(employee ? new Date(employee.hireDate) : new Date());
    setSalary(employee?.baseSalary ?? 0);
    setIsActive(employee?.isActive ?? true);
    setDob(employee?.dateOfBirth ? new Date(employee.dateOfBirth) : null);
    setGender(employee?.gender ? String(employee.gender) : null);
    setMarital(employee?.maritalStatus ? String(employee.maritalStatus) : null);
    setAddress(employee?.address ?? '');
    setEmgName(employee?.emergencyContactName ?? '');
    setEmgPhone(employee?.emergencyContactPhone ?? '');
    setBankName(employee?.bankName ?? '');
    setBankIban(employee?.bankIban ?? '');
    setBankAccount(employee?.bankAccountNumber ?? '');
  }

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        fullNameAr: nameAr.trim(),
        fullNameEn: null,
        nationalId: nationalId.trim() || null,
        phone: phone.trim() || null,
        positionAr: position.trim(),
        hireDate: toIsoDate(hireDate!),
        baseSalary: salary,
        branchId: null,
        notes: null,
        dateOfBirth: dob ? toIsoDate(dob) : null,
        gender: gender ? Number(gender) : null,
        maritalStatus: marital ? Number(marital) : null,
        address: address.trim() || null,
        emergencyContactName: emgName.trim() || null,
        emergencyContactPhone: emgPhone.trim() || null,
        bankName: bankName.trim() || null,
        bankIban: bankIban.trim() || null,
        bankAccountNumber: bankAccount.trim() || null,
      };
      if (editing) {
        return (await apiClient.put(`/api/hr/employees/${employee.employeeId}`,
          { ...body, isActive })).data;
      }
      return (await apiClient.post('/api/hr/employees', { ...body, userId: null })).data;
    },
  });

  const valid = nameAr.trim() !== '' && position.trim() !== '' && salary >= 0 && hireDate !== null;

  const submit = async () => {
    try {
      await save.mutateAsync();
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: nameAr,
      });
      onSaved();
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('hr.employees.editTitle') : t('hr.employees.create')}
      position={i18n.language === 'ar' ? 'left' : 'right'}
      size="lg"
      padding="lg"
    >
      <Tabs defaultValue="basic" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="basic">{t('hr.employees.tabs.basic')}</Tabs.Tab>
          <Tabs.Tab value="personal">{t('hr.employees.tabs.personal')}</Tabs.Tab>
          <Tabs.Tab value="bank">{t('hr.employees.tabs.bank')}</Tabs.Tab>
          {editing && <Tabs.Tab value="leave">{t('hr.employees.tabs.leave')}</Tabs.Tab>}
          {editing && <Tabs.Tab value="files">{t('hr.employees.tabs.files')}</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="basic">
          <Stack gap="md">
            <TextInput label={t('hr.employees.form.nameLabel')} withAsterisk
              value={nameAr} onChange={(e) => setNameAr(e.currentTarget.value)} />
            <TextInput label={t('hr.employees.form.positionLabel')} withAsterisk
              value={position} onChange={(e) => setPosition(e.currentTarget.value)} />
            <Group grow>
              <TextInput label={t('hr.employees.form.phoneLabel')} className="font-numeric"
                value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
              <TextInput label={t('hr.employees.form.nationalIdLabel')} className="font-numeric"
                value={nationalId} onChange={(e) => setNationalId(e.currentTarget.value)} />
            </Group>
            <Group grow>
              <DatePickerInput label={t('hr.employees.form.hireDateLabel')}
                value={hireDate} onChange={setHireDate} />
              <NumberInput label={t('hr.employees.form.salaryLabel')}
                description={t('hr.employees.form.salaryHelp')}
                value={salary} onChange={(v) => setSalary(Number(v) || 0)}
                min={0} decimalScale={2} hideControls />
            </Group>
            {editing && (
              <Switch label={t('hr.employees.active')} checked={isActive}
                onChange={(e) => setIsActive(e.currentTarget.checked)} />
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="personal">
          <Stack gap="md">
            <Group grow>
              <DatePickerInput label={t('hr.employees.form.dobLabel')}
                value={dob} onChange={setDob} clearable />
              <Select label={t('hr.employees.form.genderLabel')}
                value={gender} onChange={setGender} clearable
                data={[
                  { value: '1', label: t('hr.employees.gender.male') },
                  { value: '2', label: t('hr.employees.gender.female') },
                ]} />
            </Group>
            <Select label={t('hr.employees.form.maritalLabel')}
              value={marital} onChange={setMarital} clearable
              data={[
                { value: '1', label: t('hr.employees.marital.single') },
                { value: '2', label: t('hr.employees.marital.married') },
                { value: '3', label: t('hr.employees.marital.divorced') },
                { value: '4', label: t('hr.employees.marital.widowed') },
              ]} />
            <Textarea label={t('hr.employees.form.addressLabel')} autosize minRows={2}
              value={address} onChange={(e) => setAddress(e.currentTarget.value)} />
            <Group grow>
              <TextInput label={t('hr.employees.form.emgNameLabel')}
                value={emgName} onChange={(e) => setEmgName(e.currentTarget.value)} />
              <TextInput label={t('hr.employees.form.emgPhoneLabel')} className="font-numeric"
                value={emgPhone} onChange={(e) => setEmgPhone(e.currentTarget.value)} />
            </Group>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="bank">
          <Stack gap="md">
            <Text size="sm" c="dimmed">{t('hr.employees.form.bankHelp')}</Text>
            <TextInput label={t('hr.employees.form.bankNameLabel')}
              value={bankName} onChange={(e) => setBankName(e.currentTarget.value)} />
            <TextInput label={t('hr.employees.form.ibanLabel')} dir="ltr" className="font-numeric"
              value={bankIban} onChange={(e) => setBankIban(e.currentTarget.value)} />
            <TextInput label={t('hr.employees.form.accountLabel')} dir="ltr" className="font-numeric"
              value={bankAccount} onChange={(e) => setBankAccount(e.currentTarget.value)} />
          </Stack>
        </Tabs.Panel>

        {editing && (
          <Tabs.Panel value="leave">
            <LeaveBalancePanel employeeId={employee.employeeId} />
          </Tabs.Panel>
        )}

        {editing && (
          <Tabs.Panel value="files">
            <AttachmentsPanel
              employeeId={employee.employeeId}
              attachments={employee.attachments ?? []}
              onChanged={() => { onSaved(); void qc.invalidateQueries({ queryKey: QK }); }}
            />
          </Tabs.Panel>
        )}
      </Tabs>

      <Group justify="flex-end" gap="sm" mt="lg">
        <Button variant="default" onClick={onClose} disabled={save.isPending}>
          {t('common.cancel')}
        </Button>
        <Button onClick={submit} disabled={!valid} loading={save.isPending}>
          {editing ? t('common.save') : t('common.create')}
        </Button>
      </Group>
    </Drawer>
  );
}

function LeaveBalancePanel({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['leave-balance', employeeId] as const,
    queryFn: async () =>
      (await apiClient.get<LeaveBalanceDto>(`/api/hr/employees/${employeeId}/leave-balance`)).data,
  });

  if (isLoading || !data) {
    return <Group justify="center" py="lg"><Loader color="wheelsRed" /></Group>;
  }

  const row = (label: string, value: string) => (
    <Group justify="space-between" py={4}>
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm" fw={600} className="font-numeric">{value}</Text>
    </Group>
  );

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">{t('hr.employees.leave.lawNote')}</Text>
      <Paper p="md" withBorder>
        <Text fw={600} mb="xs">{t('hr.employees.leave.annual')}</Text>
        {row(t('hr.employees.leave.service'), `${data.yearsOfService} ${t('hr.employees.leave.years')}`)}
        {row(t('hr.employees.leave.entitlement'), `${data.annualEntitlement} ${t('hr.employees.leave.days')}`)}
        {row(t('hr.employees.leave.used'), `${data.annualUsed} ${t('hr.employees.leave.days')}`)}
        {row(t('hr.employees.leave.remaining'), `${data.annualRemaining} ${t('hr.employees.leave.days')}`)}
      </Paper>
      <Paper p="md" withBorder>
        <Text fw={600} mb="xs">{t('hr.employees.leave.sick')}</Text>
        {row(t('hr.employees.leave.fullPay'), `${data.sickFullPayRemaining} / ${data.sickFullPayEntitlement} ${t('hr.employees.leave.days')}`)}
        {row(t('hr.employees.leave.halfPay'), `${data.sickHalfPayEntitlement} ${t('hr.employees.leave.days')}`)}
      </Paper>
    </Stack>
  );
}

function AttachmentsPanel({
  employeeId, attachments, onChanged,
}: {
  employeeId: string;
  attachments: EmployeeAttachmentDto[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<string | null>('contract');
  const [uploading, setUploading] = useState(false);

  // Pick a file → upload to blob storage (files container, tenant folder) →
  // the attachment row is saved immediately. No URL pasting, no extra save.
  const uploadAndAdd = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const upload = await apiClient.post<{ url: string }>('/api/uploads/document', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return apiClient.post(`/api/hr/employees/${employeeId}/attachments`, {
        fileName: file.name,
        url: upload.data.url,
        kind,
      });
    },
    onSuccess: onChanged,
    onError: (err) => notifications.show({
      color: 'red', title: t('common.toasts.errorTitle'),
      message: (isAxiosError(err) && err.response?.status === 503)
        ? t('common.upload.notConfigured')
        : (extractApiMessage(err) ?? t('common.toasts.errorBody')),
    }),
  });

  const onPick = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadAndAdd.mutateAsync(file);
    } catch {
      // toast already shown by onError
    } finally {
      setUploading(false);
    }
  };

  const remove = useMutation({
    mutationFn: async (id: string) =>
      apiClient.delete(`/api/hr/employees/${employeeId}/attachments/${id}`),
    onSuccess: onChanged,
  });

  return (
    <Stack gap="md">
      {attachments.length === 0 ? (
        <Text size="sm" c="dimmed">{t('hr.employees.files.empty')}</Text>
      ) : (
        <Stack gap="xs">
          {attachments.map((a) => (
            <Group key={a.employeeAttachmentId} justify="space-between"
              className="rounded-md border border-[var(--wheels-border)] bg-white" px="sm" py={6}>
              <Group gap="xs">
                <IconPaperclip size={14} stroke={1.6} />
                <Anchor href={a.url} target="_blank" size="sm">{a.fileName}</Anchor>
                {a.kind && <Badge size="xs" variant="light" color="gray">{t(`hr.employees.files.kind.${a.kind}`, a.kind)}</Badge>}
              </Group>
              <ActionIcon variant="subtle" color="red" size="sm"
                aria-label={t('common.delete')}
                onClick={() => remove.mutate(a.employeeAttachmentId)}>
                <IconTrash size={14} stroke={1.6} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      )}

      <Paper p="md" withBorder>
        <Text size="sm" fw={600} mb="xs">{t('hr.employees.files.add')}</Text>
        <Text size="xs" c="dimmed" mb="sm">{t('hr.employees.files.uploadHelp')}</Text>
        <Stack gap="sm">
          <Group align="flex-end" gap="sm">
            <Select label={t('hr.employees.files.kindLabel')} value={kind} onChange={setKind}
              style={{ flex: 1 }}
              data={[
                { value: 'contract', label: t('hr.employees.files.kind.contract') },
                { value: 'id', label: t('hr.employees.files.kind.id') },
                { value: 'certificate', label: t('hr.employees.files.kind.certificate') },
                { value: 'other', label: t('hr.employees.files.kind.other') },
              ]} />
            <FileButton onChange={onPick}
              accept="application/pdf,image/jpeg,image/png,image/webp,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv">
              {(props) => (
                <Button {...props} size="xs" loading={uploading}
                  leftSection={<IconPaperclip size={14} stroke={1.8} />}>
                  {t('hr.employees.files.add')}
                </Button>
              )}
            </FileButton>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
